import './CustomerInformation.module.less';

import { ActivityLog, User } from '@kalos/kalos-rpc';
import { Button } from '@kalos/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { format } from 'date-fns';
import { type FC, useCallback, useMemo, useState } from 'react';

import { NULL_TIME } from '../../../constants';
import { useAuth } from '../../../context/AuthContext';
import { queryKeys } from '../../../hooks/react-query/constants';
import { usePendingBillingBatchGetQuery } from '../../../hooks/react-query/usePendingBillingQuery';
import { useUserGroupLinksQuery } from '../../../hooks/react-query/useUserGroupLinksQuery';
import { useUserQuery } from '../../../hooks/react-query/useUserQuery';
import {
  ActivityLogClientService,
  formatDateTime,
  UserClientService,
} from '../../../tools/helpers';
import { formatPhoneNumber } from '../../../utils/formatPhoneNumber';
import { Alert } from '../Alert';
import { ConfirmDelete } from '../ConfirmDelete';
import { CustomerEdit } from '../CustomerEdit';
import { type Data, InfoTable } from '../InfoTable';
import { Modal } from '../Modal';
import { SectionBar } from '../SectionBar';
import { CustomerNotificationDialog } from './NotificationDialog';
import { PropertyNotificationsDialog } from './PropertyNotificationsDialog';

interface Props {
  userID: number;
  viewedAsCustomer?: boolean;
  propertyId: number;
  children?: React.ReactNode;
  onClose?: () => void;
  onDelete?: () => void;
}

export const CustomerInformation: FC<Props> = ({
  userID,
  children,
  onClose,
  propertyId,
  onDelete,
}) => {
  const auth = useAuth();
  const queryClient = useQueryClient();

  interface State {
    editing: boolean;
    saving: boolean;
    deleting: boolean;
    isLoading: boolean;
    deletingSuccess: boolean;
  }

  const initialState: State = {
    editing: false,
    saving: false,
    deleting: false,
    isLoading: false,
    deletingSuccess: false,
  };

  const [state, setState] = useState<State>(initialState);
  const navigate = useNavigate();

  const handleSetState = useCallback(function handleSetState(partialState: Partial<State>) {
    setState((prevState) => {
      return { ...prevState, ...partialState };
    });
  }, []);

  const handleToggleEditing = useCallback(() => {
    setState((prev) => ({ ...prev, editing: !prev.editing }));
  }, []);

  const customerQuery = useUserQuery({
    enabled: !!userID,
    filters: {
      id: userID,
    },
  });

  const accountManagerQuery = useUserQuery({
    enabled: !!customerQuery.data?.managedBy,
    filters: {
      id: customerQuery.data?.managedBy ?? 0,
    },
  });

  const groupLinksQuery = useUserGroupLinksQuery({
    filter: {
      userId: userID,
    },
    select(data) {
      if (data)
        return {
          results: data.results,
          userGroupIds: data.results.map((el) => el.groupId),
        };
      return {
        results: [],
        userGroupIds: [],
      };
    },
  });

  const pendingBillingQuery = usePendingBillingBatchGetQuery({
    filter: {
      userId: userID,
    },
    select(data) {
      return data?.totalCount ?? 0;
    },
  });

  const isPendingBilling = pendingBillingQuery.isSuccess ? pendingBillingQuery.data > 0 : false;

  const handleResetGroups = useCallback(async () => {
    queryClient.invalidateQueries({
      queryKey: [queryKeys.userGroupLinks.root],
    });
  }, [queryClient]);

  const handleSetDeleting = useCallback(
    (deleting: boolean) => {
      handleSetState({ deleting });
    },
    [handleSetState],
  );

  const handleDelete = useCallback(async () => {
    // TODO: delete customer related data?
    queryClient.invalidateQueries({
      queryKey: [queryKeys.user.root],
    });
    const entry = User.create();
    if (customerQuery.data?.id) {
      entry.id = customerQuery.data?.id;
    }
    await UserClientService.Delete(entry);
    await ActivityLogClientService.Create(
      ActivityLog.create({
        userId: auth.user.id,
        customerId: customerQuery.data?.id,
        activityName: `Deleted Customer: ${customerQuery.data?.firstname} ${customerQuery.data?.lastname} (ID: ${customerQuery.data?.id})`,
        activityDate: format(new Date(), 'yyyy-MM-dd HH:mm:ss'),
      }),
    );
    handleSetState({ deleting: false, isLoading: false, deletingSuccess: true });
    onDelete?.();
  }, [queryClient, customerQuery.data, auth.user.id, handleSetState, onDelete]);

  const data: Data = [
    [
      {
        label: 'Name',
        value: `${customerQuery.data?.firstname ?? ''} ${customerQuery.data?.lastname ?? ''}`,
      },
      { label: 'Business Name', value: customerQuery.data?.businessname ?? '' },
    ],
    [
      {
        label: 'Primary Phone',
        value: customerQuery.data?.phone
          ? formatPhoneNumber(customerQuery.data.phone)
          : customerQuery.data?.phone,
        href: 'tel',
      },
      {
        label: 'Cell Phone',
        value: customerQuery.data?.cellphone
          ? formatPhoneNumber(customerQuery.data.cellphone)
          : customerQuery.data?.cellphone,
        href: 'tel',
      },
    ],
    [
      {
        label: 'Alternate Phone',
        value: customerQuery.data?.altphone
          ? formatPhoneNumber(customerQuery.data.altphone)
          : customerQuery.data?.altphone,
        href: 'tel',
      },
      {
        label: 'Fax',
        value: customerQuery.data?.fax
          ? formatPhoneNumber(customerQuery.data.fax)
          : customerQuery.data?.fax,
      },
    ],
    [
      {
        label: 'Billing Address',
        value: `${customerQuery.data?.address ?? ''}, ${customerQuery.data?.city ?? ''}, ${
          customerQuery.data?.state ?? ''
        } ${customerQuery.data?.zip ?? ''}`,
      },
      { label: 'Email', value: customerQuery.data?.email, href: 'mailto' },
    ],
    [
      {
        label: 'Account Manager',
        value: customerQuery.data?.managedBy
          ? accountManagerQuery.isLoading
            ? 'Loading...'
            : `${accountManagerQuery.data?.firstname ?? ''} ${accountManagerQuery.data?.lastname ?? ''}`.trim() ||
              'Unknown'
          : 'Unassigned',
      },
    ],
    [
      {
        label: 'Do Not Serve',
        value: customerQuery.data?.doNotServe ? (
          <span className="font-bold text-red-600">YES</span>
        ) : (
          'No'
        ),
      },
    ],
  ];

  const systemData: Data = [
    [
      {
        label: 'Created',
        value: customerQuery.data?.dateCreated
          ? formatDateTime(customerQuery.data.dateCreated)
          : '',
      },
    ],
    [
      {
        label: 'Last Login',
        value:
          !customerQuery.data?.lastLogin ||
          customerQuery.data?.lastLogin === null ||
          customerQuery.data?.lastLogin === NULL_TIME
            ? 'No Login Info'
            : formatDateTime(customerQuery.data.lastLogin),
      },
    ],
    [
      {
        label: 'Login ID',
        value: customerQuery.data?.login,
      },
    ],
    [
      {
        label: 'Wishes to receive promotional emails',
        value: (
          <span className="font-semibold">{customerQuery.data?.receiveemail ? 'Yes' : 'No'}</span>
        ),
      },
    ],
    [
      {
        label: 'Wishes to receive text messages',
        value: (
          <span className="font-semibold">{customerQuery.data?.multiProperty ? 'Yes' : 'No'}</span>
        ),
      },
    ],
    [
      {
        label: 'Do Not Serve',
        value: customerQuery.data?.doNotServe ? (
          <span className="font-bold text-red-600">YES</span>
        ) : (
          <span className="font-semibold">No</span>
        ),
      },
    ],
  ];

  const isManager = useMemo(() => {
    return auth.user.permissionGroups.some(
      (permission) => permission.type === 'role' && permission.name === 'Manager',
    );
  }, [auth.user.permissionGroups]);

  const { showCreateProperty } = useSearch({ strict: false });

  const showNotificationOnMount = useMemo(() => !showCreateProperty, [showCreateProperty]);

  return (
    <>
      <div className="flex flex-col lg:flex-row lg:gap-4">
        <div className="mb-4 flex-1 rounded-md border border-gray-300 lg:mb-0">
          <div className="flex items-center justify-between bg-gray-200 p-2 ">
            <h2 className="text-2xl">Customer Information</h2>
            <div className="flex flex-wrap gap-2">
              <CustomerNotificationDialog
                trigger={
                  <Button
                    disabled={!customerQuery.data?.isActive || customerQuery.isFetching}
                    size="sm"
                    className="flex-shrink-0"
                  >
                    {customerQuery.isFetching
                      ? 'Loading...'
                      : customerQuery.data?.notification && customerQuery.data?.notification != ''
                        ? 'Notification'
                        : 'Add Notification'}
                  </Button>
                }
                customerId={userID}
                showOnMount={showNotificationOnMount}
              />

              <PropertyNotificationsDialog customerId={userID} />

              <Button
                disabled={!customerQuery.data?.isActive}
                onClick={handleToggleEditing}
                size="sm"
                className="flex-shrink-0"
              >
                Edit
              </Button>
              {isManager && (
                <Button
                  disabled={!customerQuery.data?.isActive}
                  onClick={() => handleSetDeleting(true)}
                  size="sm"
                  className="flex-shrink-0"
                >
                  Delete
                </Button>
              )}
              {onClose && (
                <Button onClick={onClose} size="sm" className="flex-shrink-0">
                  Close
                </Button>
              )}
            </div>
          </div>

          {customerQuery.isLoading ? (
            <div className="p-8 text-center">
              <div className="text-gray-500">Loading customer information...</div>
            </div>
          ) : customerQuery.data?.isActive ? (
            <InfoTable data={data} loading={customerQuery.data?.id === 0} />
          ) : (
            <div className="p-8 text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gray-100">
                <svg
                  className="h-8 w-8 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7-7h14a7 7 0 00-7-7z"
                  />
                </svg>
              </div>
              <h3 className="mb-2 text-lg font-medium text-gray-900">Customer Deleted</h3>
              <p className="text-gray-500">
                This customer account has been deleted and is no longer active.
              </p>
            </div>
          )}
        </div>
        <div className="w-full rounded-md border border-gray-300 lg:w-[470px]">
          <SectionBar title="System Information" uncollapsable>
            {customerQuery.isLoading ? (
              <div className="p-6 text-center">
                <div className="text-sm text-gray-500">Loading system information...</div>
              </div>
            ) : customerQuery.data?.isActive ? (
              <InfoTable data={systemData} loading={customerQuery.data?.id === 0} />
            ) : (
              <div className="p-6 text-center">
                <p className="text-sm text-gray-500">
                  System information unavailable for deleted customers
                </p>
              </div>
            )}
          </SectionBar>
          <SectionBar
            title="Pending Billing"
            uncollapsable
            className="mb-2"
            disabled={isPendingBilling}
            actions={[
              {
                label: isPendingBilling ? 'View' : 'None',
                disabled: !isPendingBilling,
                url: isPendingBilling
                  ? propertyId
                    ? `/properties/${propertyId}/${userID}`
                    : ''
                  : '',
              },
            ]}
          >
            <InfoTable
              data={[
                [
                  {
                    label: 'Record Count',
                    value: pendingBillingQuery.data ?? 0,
                  },
                ],
              ]}
              loading={pendingBillingQuery.isPending}
            />
          </SectionBar>
        </div>
      </div>
      {children}
      <Modal open={state.editing} onClose={handleToggleEditing}>
        <CustomerEdit
          userId={customerQuery.data?.id}
          onSave={(customer) => {
            queryClient.invalidateQueries({
              queryKey: [queryKeys.user.root],
            });
            queryClient.invalidateQueries({
              queryKey: [queryKeys.userGroupLinks.root],
            });
            handleSetState({ editing: false });
            handleResetGroups();
          }}
          onClose={handleToggleEditing}
          customer={customerQuery.data}
          groupLinks={groupLinksQuery.data?.results ?? []}
        />
      </Modal>
      <ConfirmDelete
        open={state.deleting}
        disabled={customerQuery.isPending}
        onClose={() => handleSetDeleting(false)}
        onConfirm={handleDelete}
        kind="Customer"
        name={`${customerQuery.data?.firstname} ${customerQuery.data?.lastname}`}
        text=" Pending jobs, invoices and documents will not longer be accessible"
      />
      <Alert
        open={state.deletingSuccess}
        onClose={() => {
          handleSetState({ deletingSuccess: false });
          navigate({ to: '/customers' });
        }}
        title="Successful Deleting"
      >
        Customer deletion was successful!
      </Alert>
    </>
  );
};
